import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

import {
  STORE_CHECKOUT_UI_VARIATION_OPTIONS,
  shouldShowStoreCheckoutUiVariationPicker,
  type StoreCheckoutUiVariation,
} from '../constants/storeCheckout.constants';

interface IStoreCheckoutUiVariationContext {
  uiVariation: StoreCheckoutUiVariation;
  setUiVariation: (variation: StoreCheckoutUiVariation) => void;
  showUiVariationPicker: boolean;
}

const StoreCheckoutUiVariationContext = createContext<IStoreCheckoutUiVariationContext | null>(
  null
);

export const StoreCheckoutUiVariationProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const [uiVariation, setUiVariationState] = useState<StoreCheckoutUiVariation>(
    STORE_CHECKOUT_UI_VARIATION_OPTIONS[0].value
  );

  const setUiVariation = useCallback((variation: StoreCheckoutUiVariation) => {
    setUiVariationState((current) => (current === variation ? current : variation));
  }, []);

  const showUiVariationPicker = shouldShowStoreCheckoutUiVariationPicker();

  // E2E / local override: ?storeCheckoutUiVariation=default forces final-flow (shows Permanent Store)
  useEffect(() => {
    const raw = new URLSearchParams(window.location.search).get('storeCheckoutUiVariation');
    if (!raw) return;
    const match = STORE_CHECKOUT_UI_VARIATION_OPTIONS.find((opt) => opt.value === raw);
    if (match) {
      setUiVariationState(match.value);
    }
  }, []);

  const value = useMemo(
    () => ({
      uiVariation,
      setUiVariation,
      showUiVariationPicker,
    }),
    [uiVariation, setUiVariation, showUiVariationPicker]
  );

  return (
    <StoreCheckoutUiVariationContext.Provider value={value}>
      {children}
    </StoreCheckoutUiVariationContext.Provider>
  );
};

export const useStoreCheckoutUiVariation = (): IStoreCheckoutUiVariationContext => {
  const ctx = useContext(StoreCheckoutUiVariationContext);
  if (!ctx) {
    throw new Error(
      'useStoreCheckoutUiVariation must be used within StoreCheckoutUiVariationProvider'
    );
  }
  return ctx;
};

export const useStoreCheckoutUiVariationOptional = (): IStoreCheckoutUiVariationContext | null =>
  useContext(StoreCheckoutUiVariationContext);

StoreCheckoutUiVariationProvider.displayName = 'StoreCheckoutUiVariationProvider';
